/**
 * Which asset-model fields carry a citation (provenance) in the FFS Asset Model
 * step.
 *
 * This is a DENYLIST, deliberately: every asset-model field is citable unless it
 * appears below, so a field newly added to a component's
 * `*AssetModelFormFields` factory gets a source picker for free. (It used to be
 * an allowlist of 7 keys, which meant most component types rendered zero
 * pickers.)
 *
 * SCOPE — why this list is short. The gate is reached from exactly one place:
 * `FfsAssetModelEditor.renderFieldAccessory` -> `EditGeckoRichAssetModel` ->
 * `FeatureSelectionPage` (Info tab only) -> `EditableFeatureForm` ->
 * `GeckoToggleForm`, which calls it once per visible field with that field's
 * `dataKey`. Those `dataKey`s come only from the component factories dispatched
 * by `getFormFieldsForComponentType` (plus the `commonFields.ts` helpers they
 * call). So FEATURE fields (`assetModel/features/**`, rendered by `FeaturesTab`,
 * which is never handed a `renderFieldAccessory`) and ASSET-MODEL-LEVEL fields
 * (`geckoShipAssetModel.ts` etc.) can never reach this test. Do NOT pre-register
 * keys from those surfaces -- an entry that cannot fire is noise no one can
 * review. If features ever get pickers, extend this list then.
 *
 * Keys are asset-model JSON `dataKey`s -- the same stable identifiers used to
 * build the `assetModel:<componentId>:<dataKey>` provenance overlay keys (see
 * `inputProvenance.ts`). NEVER key off display labels. The match is exact and
 * case-sensitive: `geckoPipeElbow` uses `dataKey: 'Angle'` while other factories
 * use `angle`, so a future entry must spell the casing it means.
 */

/** Names and identifiers: nothing to cite. */
const IDENTITY_FIELDS = ['name', 'model_id', 'blender_model_id'] as const

/**
 * Where the component sits / how it is drawn in the viz pane. Placement is
 * authored against the 3D model, not read off a document.
 */
const PLACEMENT_FIELDS = [
  'compass_orientation',
  'clock_bearing_rotation',
  'y_start',
  'y_end',
] as const

/** Workflow bookkeeping, not asset data. */
const STATUS_FIELDS = ['stage', 'mill_plan_role'] as const

/**
 * Derived downstream rather than read off a document -- the Min Thickness step
 * computes t_min from the intrinsic inputs (`radius`, `wall_thickness`,
 * `design_pressure`, `yield_strength`, `joint_efficiency`,
 * `pressure_coefficient`), and those inputs are where the citations belong.
 */
const COMPUTED_FIELDS = ['tmin'] as const

/** Hidden in the FFS Asset Model step (supplemental loads assumed not to govern), so no citation picker. */
const SUPPLEMENTAL_LOAD_FIELDS = ['tsl'] as const

const FFS_PROVENANCE_EXCLUDED_JSONFIELDS: ReadonlySet<string> = new Set([
  ...IDENTITY_FIELDS,
  ...PLACEMENT_FIELDS,
  ...STATUS_FIELDS,
  ...COMPUTED_FIELDS,
  ...SUPPLEMENTAL_LOAD_FIELDS,
])

/** True when this asset-model field gets a source picker. */
export const fieldSupportsProvenance = (dataKey: string): boolean =>
  !FFS_PROVENANCE_EXCLUDED_JSONFIELDS.has(dataKey)
